import { service } from '@ember/service';
import { isBlank, isPresent } from '@ember/utils';
import { Ability } from 'ember-can';
import type AbilitiesService from 'ember-can/services/abilities';
import { isProductDisabled } from 'garaje/helpers/product-disabled';
import type SkinnyLocationModel from 'garaje/models/skinny-location';
import type AuthzService from 'garaje/services/authz';
import type CurrentAdminService from 'garaje/services/current-admin';
import type FeatureConfigService from 'garaje/services/feature-config';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type StateService from 'garaje/services/state';
import type UserFeatureFlagsService from 'garaje/services/user-feature-flags';
import { GLOBAL_ADMIN } from 'garaje/utils/roles';
import { Permission } from 'garaje/utils/ui-permissions';
import _intersection from 'lodash/intersection';

const CAN_VISIT_ROLES = [GLOBAL_ADMIN];

export default class GlobalAnalyticAbility extends Ability {
  @service declare currentAdmin: CurrentAdminService;
  @service declare skinnyLocations: SkinnyLocationModel;
  @service declare state: StateService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare userFeatureFlags: UserFeatureFlagsService;
  @service declare authz: AuthzService;
  @service declare abilities: AbilitiesService;
  @service declare featureConfig: FeatureConfigService;

  get canVisitAnalytics(): boolean {
    return (
      this.canVisitVisitors ||
      this.canVisitProtect ||
      this.canVisitOccupancy ||
      this.canVisitAttendance ||
      this.canVisitAttendanceByEmployee ||
      this.canVisitBenchmarking ||
      this.canVisitDeliveries ||
      this.canVisitDesks ||
      this.canVisitRooms ||
      this.canVisitParking ||
      this.canVisitScreens ||
      this.canVisitSpaceIndex ||
      this.canVisitReports ||
      this.canVisitCustomDashboards ||
      this.abilities.can('visit occupancy-global-settings') ||
      this.canVisitScheduledExports
    );
  }

  get canVisitVisitors(): boolean {
    const { vrSubscription } = this.state;
    // Company doesn't have VR
    if (isBlank(vrSubscription)) {
      return false;
    }

    return (
      !!vrSubscription?.canAccessGlobalAnalytics &&
      this.authz.hasPermissionAtCurrentCompany(Permission.ANALYTICS_GLOBAL_VISITORS_DASH_READ)
    );
  }

  get canVisitProtect(): boolean {
    const { roleNames } = this.currentAdmin;

    const canAccessProtectGlobalAnalytics =
      !!this.state.features?.canAccessProtectLegacyFeatures && !this.state.features?.canAccessOccupancyAnalytics;

    const unfilteredZoneRoles = this.state.currentUser?.zoneRoles.mapBy('roleName');
    const hasValidRole =
      isPresent(_intersection(CAN_VISIT_ROLES, roleNames)) ||
      isPresent(_intersection(CAN_VISIT_ROLES, unfilteredZoneRoles));

    return canAccessProtectGlobalAnalytics && hasValidRole;
  }

  get canVisitAnalyticsUpsell(): boolean {
    const { vrSubscription } = this.state;
    const hasCorrectRoles = isPresent(_intersection(CAN_VISIT_ROLES, this.currentAdmin.roleNames));

    return hasCorrectRoles && !vrSubscription?.canAccessGlobalAnalytics;
  }

  get canVisitDesks(): boolean {
    if (isProductDisabled('desks')) {
      return false;
    }

    const canAccessAnalytics = !!this.state.features?.canAccessDesksAnalytics;

    return canAccessAnalytics && this.authz.hasPermissionAtCurrentCompany(Permission.ANALYTICS_GLOBAL_DESKS_DASH_READ);
  }

  get canVisitOccupancy(): boolean {
    if (isProductDisabled('occupancy')) {
      return false;
    }

    return (
      !!this.state.features?.canAccessOccupancyAnalytics &&
      this.authz.hasPermissionAtCurrentCompany(Permission.ANALYTICS_GLOBAL_OCCUPANCY_DASH_READ)
    );
  }

  get canVisitReports(): boolean {
    const { vrSubscription } = this.state;
    // Company doesn't have VR
    if (isBlank(vrSubscription)) {
      return false;
    }
    return this.canVisitVisitors && !!vrSubscription?.canAccessScheduledReports;
  }

  get canVisitCustomDashboards(): boolean {
    if (isProductDisabled('analytics-v2') || isProductDisabled('workplace')) {
      return false;
    }

    if (!this.featureFlags.isEnabled('analytics-v2-custom-dashboards')) return false;

    return (
      !!this.state.features?.canAccessCustomAnalytics &&
      this.authz.hasPermissionAtCurrentCompany(Permission.ANALYTICS_GLOBAL_CUSTOM_ANALYTICS_DASH_READ)
    );
  }

  get canVisitAttendance(): boolean {
    if (isProductDisabled('workplace')) {
      return false;
    }

    const canAccessAnalytics = !!this.state.features?.canAccessAttendanceAnalytics;

    return canAccessAnalytics && this.authz.hasPermissionAtCurrentCompany(Permission.ANALYTICS_OFFICE_ATTENDANCE_READ);
  }

  get canVisitAttendanceByEmployee(): boolean {
    if (isProductDisabled('attendance')) {
      return false;
    }

    const canAccessAnalytics = !!this.state.features?.canAccessAttendanceAnalytics;

    if (!canAccessAnalytics) {
      return false;
    }

    if (this.authz.hasPermissionAtCurrentCompany(Permission.ANALYTICS_OFFICE_ATTENDANCE_READ)) {
      return true;
    }

    // Only check analytics settings if user doesn't have permission but is a manager
    if (this.featureFlags.isEnabled('analytics-manager-access')) {
      const settings = this.state?.analyticsSettingsResponse;
      return this.authz.hasManagerAccess() && (settings?.manager_access_enabled ?? false);
    }

    return false;
  }

  get canVisitDeliveries(): boolean {
    if (isProductDisabled('deliveries')) {
      return false;
    }

    const canAccessAnalytics =
      !!this.state.features?.canAccessDeliveriesAnalytics &&
      (this.featureFlags.isEnabled('sigma-embedded-analytics') ||
        this.userFeatureFlags.isEnabled('sigma-embedded-analytics-by-user'));
    return (
      canAccessAnalytics && this.authz.hasPermissionAtCurrentCompany(Permission.ANALYTICS_GLOBAL_DELIVERIES_DASH_READ)
    );
  }

  get canVisitRooms(): boolean {
    if (isProductDisabled('rooms')) {
      return false;
    }

    const canAccessAnalytics =
      !!this.state.features?.canAccessRoomsAnalytics &&
      (this.featureFlags.isEnabled('sigma-embedded-analytics') ||
        this.userFeatureFlags.isEnabled('sigma-embedded-analytics-by-user'));
    return canAccessAnalytics && this.authz.hasPermissionAtCurrentCompany(Permission.ANALYTICS_GLOBAL_ROOMS_DASH_READ);
  }

  get canCreateCustomDashboard(): boolean {
    if (isProductDisabled('analytics-v2') || isProductDisabled('workplace')) {
      return false;
    }

    return (
      !!this.state.features?.canAccessCustomAnalytics &&
      this.authz.hasPermissionAtCurrentCompany(Permission.ANALYTICS_GLOBAL_CUSTOM_ANALYTICS_DASH_CUSTOMIZE)
    );
  }

  get canVisitScheduledReportsV2(): boolean {
    if (isProductDisabled('analytics-v2') || isProductDisabled('workplace')) {
      return false;
    }

    return true;
  }

  get canVisitScheduledExports(): boolean {
    if (isProductDisabled('analytics-v2') || isProductDisabled('workplace')) {
      return false;
    }
    const permissions = [
      Permission.ANALYTICS_GLOBAL_OCCUPANCY_DASH_EXPORT,
      Permission.ANALYTICS_GLOBAL_DESKS_DASH_EXPORT,
      Permission.ANALYTICS_GLOBAL_VISITORS_DASH_EXPORT,
      Permission.ANALYTICS_GLOBAL_ROOMS_DASH_EXPORT,
      Permission.ANALYTICS_GLOBAL_DELIVERIES_DASH_EXPORT,
      Permission.ANALYTICS_OFFICE_ATTENDANCE_EXPORT,
    ];
    const hasPermission = this.authz.hasAnyPermissionAtCurrentCompany(permissions);

    return hasPermission && this.featureFlags.isEnabled('analytics-scheduled-exports');
  }

  get canVisitBenchmarking(): boolean {
    // TODO: gate once ready
    return false;
  }

  get canVisitSpaceIndex(): boolean {
    // TODO: gate once ready
    return false;
  }

  get canVisitParking(): boolean {
    if (isProductDisabled('workplace')) {
      return false;
    }

    const canAccessAnalytics =
      !!this.state.features?.canAccessParking && this.featureFlags.isEnabled('analytics-parking');

    return canAccessAnalytics && this.authz.hasPermissionAtCurrentCompany(Permission.ANALYTICS_GLOBAL_DESKS_DASH_READ);
  }

  get canVisitScreens(): boolean {
    if (isProductDisabled('screens')) {
      return false;
    }

    return (
      this.featureFlags.isEnabled('screens-analytics') &&
      this.authz.hasPermissionAtCurrentCompany(Permission.ANALYTICS_GLOBAL_OCCUPANCY_DASH_READ)
    );
  }
}
